/**
 * Volatility scenario engine data (Sprint 2b).
 * 12 anonymized chart-shape "replays" inspired by real crypto history.
 * Each scenario is a list of segments; drift is a momentum push in units of
 * coin volatility per tick (same scale as whale bias), volatilityMul scales
 * the per-tick noise while the segment is active.
 */

export interface ScenarioSegment {
  durationSec: number;
  /** momentum push in units of coin volatility per tick */
  drift: number;
  /** noise scale multiplier while this segment is active */
  volatilityMul: number;
}

export interface ScenarioConfig {
  id: string;
  segments: readonly ScenarioSegment[];
  /** ends on a pump - eligible to trigger a rug pull right after completion */
  endsWithPump?: boolean;
}

export const SCENARIOS: readonly ScenarioConfig[] = [
  // Early bull: slow grind up, then a blow-off leg
  { id: 'early_bull', endsWithPump: true, segments: [
    { durationSec: 120, drift: 0.12, volatilityMul: 0.9 },
    { durationSec: 90, drift: 0.4, volatilityMul: 1.1 },
    { durationSec: 60, drift: 2.1, volatilityMul: 1.6 },
  ]},
  // Luna-style collapse: cascading crash, then days on the floor
  { id: 'luna_collapse', segments: [
    { durationSec: 40, drift: -0.5, volatilityMul: 1.3 },
    { durationSec: 50, drift: -3.5, volatilityMul: 2.2 },
    { durationSec: 150, drift: -0.8, volatilityMul: 1.2 },
    { durationSec: 150, drift: 0, volatilityMul: 0.5 },
  ]},
  // Operator pump & dump: quiet accumulation, spike, hard exit
  { id: 'pump_and_dump', segments: [
    { durationSec: 90, drift: 0.25, volatilityMul: 0.8 },
    { durationSec: 60, drift: 2.2, volatilityMul: 1.6 },
    { durationSec: 45, drift: -3.0, volatilityMul: 2.0 },
    { durationSec: 105, drift: -0.3, volatilityMul: 0.9 },
  ]},
  // Dead-quiet sideways, then a sudden 3AM surge
  { id: 'dawn_surge', endsWithPump: true, segments: [
    { durationSec: 240, drift: 0, volatilityMul: 0.45 },
    { durationSec: 60, drift: 3.0, volatilityMul: 1.8 },
  ]},
  // Dead cat bounce: crash, hopeful bounce, second leg down
  { id: 'dead_cat', segments: [
    { durationSec: 45, drift: -2.4, volatilityMul: 1.8 },
    { durationSec: 60, drift: 1.0, volatilityMul: 1.2 },
    { durationSec: 90, drift: -1.2, volatilityMul: 1.4 },
    { durationSec: 60, drift: -0.2, volatilityMul: 0.8 },
  ]},
  // Boring crab market: nothing happens for a long time
  { id: 'boring_crab', segments: [
    { durationSec: 420, drift: 0, volatilityMul: 0.4 },
  ]},
  // Stair-step rally: push, rest, push, rest, push
  { id: 'stair_up', endsWithPump: true, segments: [
    { durationSec: 60, drift: 1.2, volatilityMul: 1.0 },
    { durationSec: 60, drift: 0, volatilityMul: 0.5 },
    { durationSec: 60, drift: 1.2, volatilityMul: 1.0 },
    { durationSec: 60, drift: 0, volatilityMul: 0.5 },
    { durationSec: 60, drift: 1.2, volatilityMul: 1.0 },
  ]},
  // Slow bleed: steady drip lower with no drama
  { id: 'slow_bleed', segments: [
    { durationSec: 360, drift: -0.3, volatilityMul: 0.7 },
  ]},
  // Double top: two failed peaks, then breakdown
  { id: 'double_top', segments: [
    { durationSec: 60, drift: 1.5, volatilityMul: 1.3 },
    { durationSec: 60, drift: -1.2, volatilityMul: 1.2 },
    { durationSec: 60, drift: 1.4, volatilityMul: 1.3 },
    { durationSec: 90, drift: -1.6, volatilityMul: 1.5 },
  ]},
  // Capitulation V: violent flush straight into a V-shaped recovery
  { id: 'capitulation_v', segments: [
    { durationSec: 60, drift: -2.6, volatilityMul: 2.0 },
    { durationSec: 60, drift: 2.2, volatilityMul: 1.6 },
    { durationSec: 120, drift: 0.3, volatilityMul: 0.9 },
  ]},
  // FOMO spike: calm drift, vertical candle, fast giveback
  { id: 'fomo_spike', segments: [
    { durationSec: 150, drift: 0.1, volatilityMul: 0.6 },
    { durationSec: 30, drift: 3.5, volatilityMul: 2.2 },
    { durationSec: 60, drift: -1.8, volatilityMul: 1.6 },
  ]},
  // Whipsaw: violent chop in both directions
  { id: 'whipsaw', segments: [
    { durationSec: 45, drift: 1.6, volatilityMul: 1.7 },
    { durationSec: 45, drift: -1.6, volatilityMul: 1.7 },
    { durationSec: 45, drift: 1.6, volatilityMul: 1.7 },
    { durationSec: 45, drift: -1.6, volatilityMul: 1.7 },
    { durationSec: 60, drift: 0, volatilityMul: 1.0 },
  ]},
];

export function getScenario(id: string): ScenarioConfig | undefined {
  return SCENARIOS.find(s => s.id === id);
}

/** Total scenario duration in ms */
export function scenarioDuration(scenario: ScenarioConfig): number {
  return scenario.segments.reduce((sum, seg) => sum + seg.durationSec * 1000, 0);
}

/** Segment active at the given elapsed ms, or null when the scenario is over */
export function segmentAt(scenario: ScenarioConfig, elapsedMs: number): ScenarioSegment | null {
  if (elapsedMs < 0) return null;
  let acc = 0;
  for (const seg of scenario.segments) {
    acc += seg.durationSec * 1000;
    if (elapsedMs < acc) return seg;
  }
  return null;
}

export const SCENARIO_CONFIG = {
  /** rest period between scenarios per coin (60~180s per spec) */
  restMin: 60_000,
  restMax: 180_000,
  /** first assignment delay after a fresh state */
  firstDelayMin: 15_000,
  firstDelayMax: 90_000,
  /** balance guard: at/below minPrice*ratio ignore drift, push recovery */
  guardPriceRatio: 1.1,
  /** recovery drift (volatility units per tick) while the guard is active */
  recoveryDrift: 1.0,
} as const;
